import React from 'react';
import { Wrench, ShieldCheck, PoundSterling, HardHat } from 'lucide-react';

/**
 * "Our Promise" section — a premium, editorial-style commitment card.
 * All copy is compliance-safe: no absolute guarantees, no unverifiable claims,
 * no 24/7 statements, no manufacturer partnership implication. Reflects the
 * actual maintenance-service-plan operating model.
 */

const PROMISES = [
  {
    icon: Wrench,
    kicker: '01',
    title: 'Fixed or replaced',
    text: 'If an eligible appliance breaks down, we arrange a repair through our UK network — and where the item cannot reasonably be repaired, replacement is available in line with your plan Terms & Conditions.',
  },
  {
    icon: ShieldCheck,
    kicker: '02',
    title: 'Same UK team, always',
    text: 'Our in-house customer-service team is based in the United Kingdom. When you call or email us, you speak to a real person from Domestic Home Services Limited — never an offshore call centre.',
  },
  {
    icon: PoundSterling,
    kicker: '03',
    title: 'Clear, honest pricing',
    text: 'One plan, one price. Monthly or annual — you decide. No credit checks, no hidden renewal jumps, and every charge (including any excess or admin fee) is stated up-front on your Certificate of Plan.',
  },
  {
    icon: HardHat,
    kicker: '04',
    title: 'Qualified engineers',
    text: 'Repair and maintenance visits are arranged through our network of independent, qualified UK service providers, chosen for their expertise on the appliances and systems your plan covers.',
  },
];

const STATS = [
  { top: '12 months', bot: 'Parts & labour under plan T&Cs' },
  { top: 'Mon–Fri', bot: '9am–5pm UK customer service' },
  { top: 'Qualified', bot: 'UK engineer network' },
  { top: '14-day', bot: 'Cooling-off period on every plan' },
];

const Promises = () => {
  return (
    <section className="relative py-20 md:py-28 bg-[#FFF6EB] overflow-hidden">
      {/* subtle background flourishes */}
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#FCE0EC] blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#E8DDFF] blur-3xl opacity-70 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5">
        {/* Section header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 bg-white border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C39]" /> Our promise
            </div>
            <h2 className="text-4xl md:text-6xl lg:text-[68px] leading-[1.02] font-bold text-[#1F0068] tracking-tight">
              The <span className="italic text-[#FF5C39]">Domestic Home Services</span> care promise
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-lg text-[#1F0068]/70 leading-relaxed">
              Every plan we sell is a maintenance service agreement — <b>not</b> an insurance product. That means straightforward cover, straightforward pricing and a real UK team on the phone when something breaks.
            </p>
          </div>
        </div>

        {/* Promise cards — asymmetric 2x2 with signature editorial feel */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {PROMISES.map((p, i) => {
            const Ico = p.icon;
            // Alternate emphasis for editorial variation without going gimmicky
            const isAccent = i === 0 || i === 3;
            return (
              <div
                key={p.title}
                data-testid={`promise-card-${i}`}
                className={`group relative rounded-[28px] p-8 md:p-10 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${isAccent ? 'bg-[#1F0068] text-white' : 'bg-white text-[#1F0068] border border-[#1F0068]/8'}`}
              >
                <div className={`absolute top-0 right-0 text-[130px] font-black leading-none opacity-[0.06] tracking-tighter pointer-events-none ${isAccent ? 'text-white' : 'text-[#1F0068]'}`}>{p.kicker}</div>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${isAccent ? 'bg-[#FF5C39] text-white' : 'bg-[#FFEDD6] text-[#FF5C39]'}`}>
                  <Ico className="w-6 h-6" strokeWidth={2.25} />
                </div>
                <div className={`text-[10px] uppercase tracking-[0.28em] font-bold mb-2 ${isAccent ? 'text-[#FF5C39]' : 'text-[#FF5C39]'}`}>Promise · {p.kicker}</div>
                <h3 className="text-2xl md:text-3xl font-bold leading-[1.1] tracking-tight mb-3">{p.title}</h3>
                <p className={`text-[15px] leading-relaxed ${isAccent ? 'text-white/85' : 'text-[#1F0068]/75'}`}>{p.text}</p>
              </div>
            );
          })}
        </div>

        {/* Trust stats bar */}
        <div className="mt-6 md:mt-8 rounded-[28px] bg-white border border-[#1F0068]/8 p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`promise-stat-${i}`} className={`text-center ${i < STATS.length - 1 ? 'md:border-r md:border-[#1F0068]/10' : ''}`}>
              <div className="text-3xl md:text-4xl font-extrabold text-[#1F0068] tracking-tight">{s.top}</div>
              <div className="text-[11px] uppercase tracking-widest text-[#1F0068]/60 font-bold mt-2 leading-tight">{s.bot}</div>
            </div>
          ))}
        </div>

        {/* Fine-print — compliance clarity */}
        <p className="mt-6 text-[11px] text-[#1F0068]/55 max-w-3xl leading-relaxed">
          Repairs, replacements and response times are provided in accordance with your plan Terms &amp; Conditions and subject to engineer availability. All plans are maintenance service agreements — Domestic Home Services Limited does not provide insurance products.
        </p>
      </div>
    </section>
  );
};

export default Promises;
